import { PublicLayout } from '@/components/layouts/public-layout.component';
import {
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import type { NextPage } from 'next';
import type { ReactNode } from 'react';

function Callout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderLeft: 4,
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
        p: 2,
        borderRadius: 1
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function SectionHeading({ num, title }: { num: number; title: string }) {
  return (
    <Stack sx={{ gap: 1, mt: 2 }}>
      <Divider />
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {num}. {title}
      </Typography>
    </Stack>
  );
}

function SubHeading({ id, title }: { id: string; title: string }) {
  return (
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
      {id} {title}
    </Typography>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <Stack sx={{ gap: 0.5, pl: 1 }}>
      {items.map((item) => (
        <Typography key={item} variant="body1">
          • {item}
        </Typography>
      ))}
    </Stack>
  );
}

const Privacy: NextPage = () => {
  return (
    <PublicLayout title="Legal & Privacy - FraudFront">
      <Container maxWidth="md">
        <Stack sx={{ gap: 4, my: 4 }}>
          {/* Header */}
          <Stack sx={{ gap: 1 }}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
              FraudFront
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              Powered by Iris
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Legal & Privacy Documentation
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              Plain English. Honest Commitments. Your Rights, Clearly Stated.
            </Typography>
          </Stack>

          <Callout title="Our Public Benefit Mandate">
            <Typography variant="body2">
              To advance human wellbeing and connection through private, safe,
              and trustable technology that empowers people and communities.
              This document is an expression of that mandate — not legal fine
              print designed to protect us from you. We are on your side.
            </Typography>
          </Callout>

          <Stack sx={{ gap: 0.5 }}>
            <Typography variant="body1">
              <strong>Effective Date:</strong> June 2026
            </Typography>
            <Typography variant="body1">
              <strong>Questions?</strong>{' '}
              <Link href="mailto:privacy@fraud-front.com">
                privacy@fraud-front.com
              </Link>
            </Typography>
          </Stack>

          {/* Table of contents */}
          <Stack sx={{ gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {"What's Inside This Document"}
            </Typography>
            <Typography variant="body1">
              We know nobody reads legal documents. {"That's"} actually part of
              the problem — companies hide things in them. {"We're"} not doing
              that. {"Here's"} exactly {"what's"} in here and where to find it:
            </Typography>
            <Bullets
              items={[
                "1. Terms of Service — What We Do, What We Don't, What You Agree To",
                "2. Privacy Policy — What We Collect, Why, and What We'll Never Do With It",
                '3. Iris Advisor Authorization — Understanding Your AI Family Member',
                '4. Circle of Trust Authorization — Who You Invite, What They See',
                '5. Consent & Data Authorization — Your Explicit Permissions',
                '6. Incident Response Policy — What Happens If Something Goes Wrong',
                '7. Data Processing Agreement — The Technical Commitment'
              ]}
            />
          </Stack>

          <Callout title="A Note Before You Read">
            <Typography variant="body2">
              FraudFront is a Public Benefit Corporation. That means we are
              legally required to serve your interests, not just our
              shareholders. Every clause in this document reflects that
              obligation. If you ever feel something is unclear or unfair, email
              us at{' '}
              <Link href="mailto:privacy@fraud-front.com">
                privacy@fraud-front.com
              </Link>
              . We read every message.
            </Typography>
          </Callout>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* 1. Terms of Service */}
          {/* ═══════════════════════════════════════════════════════ */}
          <SectionHeading num={1} title="Terms of Service" />

          <Callout title="The Short Version">
            <Typography variant="body2">
              FraudFront helps protect you and your family from fraud. Iris, our
              AI advisor, flags suspicious communications and alerts your circle
              of trust. She advises — she does not guarantee. You are always in
              control.
            </Typography>
          </Callout>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="1.1" title="Who We Are" />
            <Typography variant="body1">
              FraudFront is a Public Benefit Corporation dedicated to protecting
              families — especially older Americans — from the growing threat of
              AI-powered fraud. We build tools that work across the channels
              scammers use: email, SMS, browsers, and messaging apps. Our AI
              advisors, Iris and Dahlia, partner together as your family's
              always-on fraud experts.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="1.2" title="Who Can Use FraudFront" />
            <Typography variant="body1">
              You must be 18 years or older to create a FraudFront account. By
              creating an account, you confirm you meet this requirement. A
              parent, adult child, or trusted family member may purchase a
              FraudFront subscription on behalf of an older family member, but
              the older family member must set up their own account and add
              their own circle of trust. We do not allow third parties to set up
              accounts on behalf of others without the primary user's knowledge
              and active participation.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="1.3" title="What FraudFront Does" />
            <Typography variant="body1">
              FraudFront monitors the communication channels you authorize —
              email, SMS, your browser, and messaging apps — for patterns
              consistent with fraud and social engineering attacks. When Iris
              detects a suspicious pattern, it:
            </Typography>
            <Bullets
              items={[
                'Flags the communication as suspicious',
                'Moves it to a review area for your attention',
                'Explains in plain language what it found and why it concerns it',
                'Alerts members of your circle of trust if escalation is appropriate',
                'Tracks the full attack chain across channels — not just one message in isolation'
              ]}
            />
            <Typography variant="body1">
              {"FraudFront's"} cross-channel intelligence means we can see when
              a scammer who started in email tries to move you to WhatsApp, or
              when a suspicious link in SMS leads to a fake browser page.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="1.4" title="What FraudFront Does NOT Do" />
            <Callout title="Important Limitations — Please Read">
              <Typography variant="body2">
                Iris is an advisor, not a guarantor. FraudFront does not promise
                to catch every fraud attempt. We do not permanently delete
                communications without your instruction. We do not make
                financial decisions for you. We do not contact law enforcement
                on your behalf without your explicit request. We are a tool that
                helps you make better decisions — the final decision is always
                yours.
              </Typography>
            </Callout>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="1.5" title="Subscription and Billing" />
            <Typography variant="body1">
              FraudFront is available for $28 per month per user. A user account
              covers one primary user and their designated circle of trust.
              Billing is monthly and you may cancel at any time. Cancellation
              takes effect at the end of your current billing period. We do not
              offer refunds for partial months, but we will never charge you
              after you cancel.
            </Typography>
            <Typography variant="body1">
              Free access is available for qualified elder care facilities, Area
              Agencies on Aging, and community organizations. Contact us at{' '}
              <Link href="mailto:partnerships@fraud-front.com">
                partnerships@fraud-front.com
              </Link>{' '}
              to apply.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="1.6" title="Limitation of Liability" />
            <Typography variant="body1">
              FraudFront provides fraud detection and advisory services. We are
              not an insurance product. We do not guarantee the recovery of lost
              funds. In the event that FraudFront fails to flag a fraudulent
              communication that results in financial loss, our liability is
              limited to the fees you paid to FraudFront in the three months
              preceding the incident. This limitation exists not to protect
              ourselves at your expense, but because guaranteeing complete fraud
              prevention is not technically possible for any service — and we
              will not make promises we cannot keep.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="1.7" title="Termination" />
            <Typography variant="body1">
              You may cancel your account at any time through your account
              settings or by emailing{' '}
              <Link href="mailto:support@fraud-front.com">
                support@fraud-front.com
              </Link>
              . Upon cancellation we will delete your personal data within 30
              days unless you request an export first. FraudFront may terminate
              accounts that are used in violation of these terms, with 30 days
              notice except in cases of fraud or abuse.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="1.8" title="Dispute Resolution" />
            <Typography variant="body1">
              If you have a dispute with FraudFront, please contact us at{' '}
              <Link href="mailto:support@fraud-front.com">
                support@fraud-front.com
              </Link>
              . We commit to responding within 5 business days and working in
              good faith to resolve any issue. If we cannot resolve the dispute
              directly, disputes shall be resolved through binding arbitration
              in California, except that you retain the right to bring
              individual claims in small claims court. We will never use
              mandatory arbitration to prevent you from asserting your rights.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="1.9" title="Governing Law" />
            <Typography variant="body1">
              These terms are governed by California law. Users in all states
              retain rights provided by their applicable state consumer privacy
              laws. As FraudFront expands operations into additional states, we
              will update this document to include specificities.
            </Typography>
          </Stack>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* 2. Privacy Policy */}
          {/* ═══════════════════════════════════════════════════════ */}
          <SectionHeading num={2} title="Privacy Policy" />

          <Callout title="The Short Version">
            <Typography variant="body2">
              We collect data to protect you. We share data only with people you
              choose. We will never sell your data. Ever. Not now. Not if we get
              acquired. Not ever. This is a Public Benefit Corporation
              commitment, not just a policy.
            </Typography>
          </Callout>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="2.1" title="What We Collect and Why" />
            <Typography variant="body1">
              We collect only what we need to protect you. Here is exactly what
              that means:
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    What We Collect
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Why We Collect It
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(
                  [
                    [
                      'Email data and content you authorize',
                      'To detect fraud patterns in your inbox'
                    ],
                    [
                      'SMS messages and notifications on enrolled devices',
                      'To catch smishing and SMS-based social engineering'
                    ],
                    [
                      'Browser content via our plugin',
                      'To block malicious links before you click them'
                    ],
                    [
                      'Messaging app activity on enrolled channels',
                      'To track cross-channel attack chains'
                    ],
                    [
                      'Account information (name, email, phone)',
                      'To maintain your account and contact you'
                    ],
                    [
                      'Device identifiers for enrolled endpoints',
                      'To know which devices are protected'
                    ],
                    [
                      'Communication pattern data across channels',
                      'To build network intelligence that protects all users'
                    ],
                    [
                      'Circle of trust member contact information',
                      'To alert your trusted family members when needed'
                    ]
                  ] as [string, string][]
                ).map(([what, why]) => (
                  <TableRow key={what}>
                    <TableCell>{what}</TableCell>
                    <TableCell>{why}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="2.2" title="What We Will Never Do With Your Data" />
            <Typography variant="body1">
              These are absolute commitments, not subject to change without your
              explicit consent:
            </Typography>
            <Bullets
              items={[
                'We will never sell your personal data to any third party',
                'We will never use your data for targeted advertising',
                'We will never share your communication content with anyone outside your circle of trust without a court order',
                'We will never use your data to train AI models without your explicit opt-in consent',
                'We will never retain your data after account deletion beyond the 30-day deletion window',
                'We will never share data with your employer, insurance company, or government agency without a valid legal process'
              ]}
            />
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading
              id="2.3"
              title="Network Intelligence and the Data Moat"
            />
            <Typography variant="body1">
              {"FraudFront's"} power comes from seeing fraud patterns across our
              entire network. When a scammer targets one of our users, we learn
              from that attack and protect every other user. The more families
              we protect, the smarter Iris becomes.
            </Typography>
            <Typography variant="body1">
              The data we use for this network intelligence is anonymized and
              aggregated. We use patterns, not personal details. Your
              {"grandmother's"} specific conversation is never shared with other
              users — but the attack signature that scammers use is added to{' '}
              {"Iris's"} knowledge base to protect other families.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="2.4" title="Your Rights — All States" />
            <Typography variant="body1">
              Regardless of which state you live in, you have these rights with
              FraudFront:
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Your Right</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>What It Means</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(
                  [
                    [
                      'Right to Access',
                      'Request a full copy of all data we hold about you'
                    ],
                    [
                      'Right to Delete',
                      'Ask us to delete your account and all associated data'
                    ],
                    [
                      'Right to Correct',
                      'Fix any inaccurate information in your account'
                    ],
                    [
                      'Right to Portability',
                      'Export your data in a machine-readable format'
                    ],
                    [
                      'Right to Opt Out',
                      'Opt out of network intelligence data sharing at any time'
                    ],
                    [
                      'Right to Know',
                      'Know exactly what data we collected in the past 12 months'
                    ],
                    [
                      'Right to Non-Discrimination',
                      'We will never treat you differently for exercising your rights'
                    ]
                  ] as [string, string][]
                ).map(([right, meaning]) => (
                  <TableRow key={right}>
                    <TableCell>{right}</TableCell>
                    <TableCell>{meaning}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="body1">
              To exercise any of these rights, email{' '}
              <Link href="mailto:privacy@fraud-front.com">
                privacy@fraud-front.com
              </Link>{' '}
              with the subject line &quot;Privacy Request.&quot; We will respond
              within 45 days. We will never charge you for these requests.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading
              id="2.5"
              title="California-Specific Rights (CCPA/CPRA)"
            />
            <Typography variant="body1">
              California residents have additional rights under the California
              Consumer Privacy Act and California Privacy Rights Act. You have
              the right to know the specific categories of personal information{' '}
              {"we've"} collected, the right to correct inaccurate information,
              and the right to limit our use of sensitive personal information.
              We do not sell or share personal information as defined under
              CCPA. A &quot;Do Not Sell or Share My Personal Information&quot;
              option is available in your account settings even though we do not
              engage in these practices.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="2.6" title="Massachusetts-Specific Commitments" />
            <Typography variant="body1">
              FraudFront maintains a Written Information Security Program (WISP)
              in compliance with 201 CMR 17.00. All personal data is encrypted
              in transit and at rest. We maintain documented incident response
              procedures. In the event of a data breach affecting Massachusetts
              residents, we will notify affected individuals within 30 days.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="2.7" title="Data Retention" />
            <Typography variant="body1">
              We keep your data for as long as you are a FraudFront member. Upon
              cancellation, we delete your personal data within 30 days.
              Anonymized network intelligence data derived from your usage may
              be retained to protect other users, but it cannot be traced back
              to you. Communication content is not retained longer than 90 days
              unless you explicitly save a flagged communication to your alert
              history.
            </Typography>
          </Stack>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* 3. Iris Advisor Authorization */}
          {/* ═══════════════════════════════════════════════════════ */}
          <SectionHeading num={3} title="Iris Advisor Authorization" />

          <Callout title="Think of Iris This Way">
            <Typography variant="body2">
              Iris is like having a family member who&apos;s spent years in
              fraud prevention at the world&apos;s biggest tech companies — and
              never sleeps, never panics, and always picks up the phone. It will
              tell you what it sees and what it thinks you should do. But it
              respects that the decision is always yours.
            </Typography>
          </Callout>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="3.1" title="What Iris Is" />
            <Typography variant="body1">
              Iris is {"FraudFront's"} AI advisor. It analyzes your authorized
              communications across all enrolled channels, identifies patterns
              consistent with fraud and social engineering, and communicates its
              findings to you in plain, conversational language. It can answer
              your questions, explain why something looks suspicious, and help
              you and your family understand {"what's"} happening.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="3.2" title="What Iris Is Not" />
            <Typography variant="body1">
              Iris is an advisor. It is not a guarantee. It is not infallible.
              It is not a replacement for human judgment. It will sometimes flag
              things that are not fraud — false positives happen. It may
              occasionally miss something — false negatives happen too. We are
              continuously improving its accuracy, and your network helps every
              other {"family's"} Iris get smarter.
            </Typography>
            <Callout title="Iris's Role: Advisor, Not Decision Maker">
              <Typography variant="body2">
                Iris flags. Iris explains. Iris alerts. It does not permanently
                delete communications. It does not transfer funds. It does not
                contact third parties without your instruction. Every action
                Iris takes is reversible by you. You are always in control.
              </Typography>
            </Callout>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="3.3" title="How Iris Works" />
            <Typography variant="body1">
              Iris monitors your enrolled channels in real time. When it detects
              a suspicious pattern, it:
            </Typography>
            <Bullets
              items={[
                'Moves the communication to a Suspicious folder for your review',
                'Sends you a plain-language explanation of what was found',
                'Tracks whether the same attacker tries to reach you on other channels',
                'Escalates to your circle of trust if the attack continues or intensifies',
                'Learns from the interaction to better protect you and other FraudFront families'
              ]}
            />
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="3.4" title="Iris and AI Technology" />
            <Typography variant="body1">
              Iris is built on advanced AI technology. Like all AI systems, it
              is not perfect. Iris recommendations are based on pattern
              recognition across our network and should be treated as informed
              advice, not definitive conclusions. When in doubt, trust your
              instincts and consult a trusted family member or your circle of
              trust.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="3.5" title="Interacting With Iris" />
            <Typography variant="body1">
              You can talk to Iris conversationally at any time. Please ask it
              why something was flagged. Ask Iris what a specific type of scam
              looks like and how to protect your friends and loved ones. Ask
              Iris to explain a suspicious message to you or a family member.
              Iris is designed to <strong>educate</strong> and{' '}
              <strong>empower</strong>, not just to alert. The more you interact
              with Iris, the more your whole family learns how to recognize and
              avoid fraud.
            </Typography>
          </Stack>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* 4. Circle of Trust Authorization */}
          {/* ═══════════════════════════════════════════════════════ */}
          <SectionHeading num={4} title="Circle of Trust Authorization" />

          <Callout title="The User is Sovereign">
            <Typography variant="body2">
              The primary account holder — the person being protected — always
              controls their circle of trust. A family member may purchase a
              FraudFront subscription as a gift, but they cannot set up the
              circle of trust on behalf of the primary user. The primary user
              adds who they want. The primary user can remove anyone at any
              time. No exceptions.
            </Typography>
          </Callout>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="4.1" title="What the Circle of Trust Is" />
            <Typography variant="body1">
              Your circle of trust is the group of people you choose to receive
              alerts about your account. When Iris detects a suspicious pattern
              that you may not have seen, or when an attack escalates across
              multiple channels, Iris can notify your circle of trust so they
              can reach out and help.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="4.2" title="How the Circle of Trust Works" />
            <Bullets
              items={[
                'The primary account holder creates and manages their circle of trust',
                'Circle members receive alerts only — they cannot access your communication content',
                'Circle members are notified that you received a suspicious communication and that you may want to talk',
                'Circle members cannot make changes to your account',
                'You can add or remove circle members at any time with immediate effect'
              ]}
            />
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="4.3" title="What Circle Members See" />
            <Typography variant="body1">
              Circle members receive alerts, not content. Here is the
              distinction:
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    What Circle Members See
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    What Circle Members Do NOT See
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(
                  [
                    [
                      'Alert: Suspicious communication detected',
                      'The actual content of messages'
                    ],
                    [
                      'Type of scam Iris identified',
                      'Full communication history'
                    ],
                    [
                      'Whether the attack is escalating',
                      'Financial account information'
                    ],
                    [
                      'Recommendation to check in with you',
                      'Personal health information'
                    ],
                    [
                      'Date and time of the alert',
                      'Other circle member identities'
                    ]
                  ] as [string, string][]
                ).map(([see, notSee]) => (
                  <TableRow key={see}>
                    <TableCell>{see}</TableCell>
                    <TableCell>{notSee}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="4.4" title="Removing Circle Members" />
            <Typography variant="body1">
              You may remove any circle member at any time through your account
              settings. Removal is immediate. Removed members will not receive
              future alerts and cannot see historical alerts after removal. If
              you need assistance removing a circle member, contact{' '}
              <Link href="mailto:support@fraud-front.com">
                support@fraud-front.com
              </Link>{' '}
              and we will action the removal within one business day.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="4.5" title="Circle Member Consent" />
            <Typography variant="body1">
              When you add someone to your circle of trust, they will receive an
              invitation explaining what FraudFront is, what kinds of alerts
              they will receive, and how to opt out. Circle membership requires
              their active acceptance. We will not add anyone to your circle who
              has not agreed to participate.
            </Typography>
          </Stack>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* 5. Consent & Data Authorization */}
          {/* ═══════════════════════════════════════════════════════ */}
          <SectionHeading num={5} title="Consent & Data Authorization" />

          <Callout title="What Your Consent Covers">
            <Typography variant="body2">
              By creating a FraudFront account and enrolling devices and
              channels, you are giving us permission to monitor those channels
              on your behalf. This section explains exactly what that means. You
              can withdraw consent for any channel at any time without closing
              your account.
            </Typography>
          </Callout>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="5.1" title="Channel-by-Channel Authorization" />
            <Typography variant="body1">
              FraudFront monitors only the channels you specifically enroll.
              Here is what you are authorizing for each:
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Channel</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {"What You're Authorizing"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(
                  [
                    [
                      'Email',
                      'Iris reads incoming messages to detect phishing, spoofing, and social engineering'
                    ],
                    [
                      'SMS / Text Messages',
                      'Iris monitors incoming texts for smishing and scam patterns'
                    ],
                    [
                      'Browser (Chrome Plugin)',
                      'Iris checks content and links before you click and flags malicious pages'
                    ],
                    [
                      'Social Messengers',
                      'Iris monitors incoming messages for fraud patterns'
                    ],
                    [
                      'Cross-Channel Tracking',
                      'Iris monitors for escalation attempts from other channels'
                    ],
                    [
                      'Other Messaging Apps',
                      'Only apps you explicitly enroll are monitored'
                    ]
                  ] as [string, string][]
                ).map(([channel, auth]) => (
                  <TableRow key={channel}>
                    <TableCell>{channel}</TableCell>
                    <TableCell>{auth}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="body1">
              You may enroll or unenroll any channel at any time through your
              account settings. Unenrollment takes effect immediately.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="5.2" title="Cross-Channel Monitoring" />
            <Typography variant="body1">
              One of {"FraudFront's"} unique capabilities is cross-channel
              intelligence — the ability to track an attack as it moves from one
              platform to another. For example, a scammer who contacts you by
              email and then asks you to continue on WhatsApp. You explicitly
              authorize FraudFront to correlate data across your enrolled
              channels to identify these cross-channel attacks. This correlation
              is performed only across channels you have enrolled and only for
              fraud detection purposes.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="5.3" title="California Wiretapping Notice" />
            <Typography variant="body1">
              California Penal Code Section 631 governs electronic communication
              interception. FraudFront monitors communications received by you,
              with your explicit consent, on your enrolled devices and channels.
              We monitor the receiving end of communications — we are not
              intercepting communications in transit. This is analogous to a
              person reading their own mail with the help of an advisor. Your
              consent is the legal basis for this monitoring. You should be
              aware that persons who send you communications may not have
              consented to Iris analyzing those communications. If you want any
              particular systems removed from analysis, please contact{' '}
              <Link href="mailto:support@fraud-front.com">
                support@fraud-front.com
              </Link>{' '}
              to get a deny rule enabled.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="5.4" title="Withdrawing Consent" />
            <Typography variant="body1">
              You may withdraw consent for any channel, feature, or the entire
              service at any time. To withdraw consent for specific channels,
              use your account settings. To withdraw all consent and close your
              account, email{' '}
              <Link href="mailto:support@fraud-front.com">
                support@fraud-front.com
              </Link>{' '}
              with the subject &quot;Account Closure.&quot; We will confirm
              closure within 24 hours and delete your data within 30 days.
            </Typography>
          </Stack>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* 6. Incident Response Policy */}
          {/* ═══════════════════════════════════════════════════════ */}
          <SectionHeading num={6} title="Incident Response Policy" />

          <Callout title="Our Commitment">
            <Typography variant="body2">
              If something goes wrong — a data breach, a security incident, a
              failure in our systems — we will tell you what happened, when it
              happened, what data was affected, what we are doing about it, and
              what you should do. We will not hide behind legal language or
              delay communication to manage our reputation.
            </Typography>
          </Callout>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="6.1" title="What Constitutes an Incident" />
            <Bullets
              items={[
                'Unauthorized access to FraudFront systems containing personal data',
                'A breach of personal data affecting any FraudFront user',
                "A failure in Iris's fraud detection affecting user security",
                'Unauthorized disclosure of circle of trust alert data',
                'Loss or theft of devices containing unencrypted personal data'
              ]}
            />
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="6.2" title="Our Response Timeline" />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Timeframe</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    What We Will Do
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(
                  [
                    [
                      'Within 24 hours of detection',
                      'Internal incident team activated, scope assessed'
                    ],
                    [
                      'Within 48 hours',
                      'Affected users notified by email with plain language explanation'
                    ],
                    [
                      'Within 30 days (MA requirement)',
                      'Massachusetts residents receive formal breach notification'
                    ],
                    [
                      'Within 45 days (CA/UT requirement)',
                      'Full incident report available to all affected users'
                    ],
                    ['Ongoing', 'Updates provided as investigation progresses']
                  ] as [string, string][]
                ).map(([timeframe, action]) => (
                  <TableRow key={timeframe}>
                    <TableCell>{timeframe}</TableCell>
                    <TableCell>{action}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="6.3" title="What We Will Tell You" />
            <Typography variant="body1">
              Our breach notification will include: what happened, when we
              discovered it, what types of data were involved, what we have done
              to stop it, what you should do to protect yourself, and a direct
              contact for questions. We will not use this communication to
              minimize what happened or to redirect blame.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="6.4" title="Law Enforcement" />
            <Typography variant="body1">
              FraudFront will cooperate with law enforcement investigations
              involving our platform. We will disclose personal data to law
              enforcement <strong>only in response</strong> to valid legal
              process — a court order, subpoena, or warrant. We will notify
              affected users of law enforcement requests for their data unless
              we are legally prohibited from doing so. We will never voluntarily
              hand over personal data to government agencies without legal
              process.
            </Typography>
          </Stack>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* 7. Data Processing Agreement */}
          {/* ═══════════════════════════════════════════════════════ */}
          <SectionHeading num={7} title="Data Processing Agreement" />

          <Callout title="Who This Is For">
            <Typography variant="body2">
              This section applies to elder care facilities, Area Agencies on
              Aging, and community organizations using {"FraudFront's"} free
              tier. If you are an individual consumer, the Privacy Policy above
              covers your data relationship with us.
            </Typography>
          </Callout>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="7.1" title="Roles and Responsibilities" />
            <Typography variant="body1">
              In this agreement, the partner organization is the Data Controller
              — you determine the purposes for which personal data is processed.
              FraudFront is the Data Processor — we process data only on your
              documented instructions.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="7.2" title="What We Process on Your Behalf" />
            <Bullets
              items={[
                'Account information for enrolled individuals',
                'Communication data for enrolled channels',
                'Alert and incident data generated by Iris',
                'Circle of trust configurations'
              ]}
            />
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading
              id="7.3"
              title="Our Commitments to Partner Organizations"
            />
            <Bullets
              items={[
                'Process data only on documented instructions from the Controller',
                'Ensure all personnel with data access are bound by confidentiality obligations',
                'Implement appropriate technical and organizational security measures',
                'Notify the Controller within 24 hours of becoming aware of a personal data breach',
                'Delete or return all personal data upon termination of the partnership',
                'Provide all information necessary to demonstrate compliance with this agreement'
              ]}
            />
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="7.4" title="Sub-processors" />
            <Typography variant="body1">
              FraudFront may engage sub-processors to deliver our service (for
              example, cloud infrastructure providers). We maintain a current
              list of sub-processors at fraudfront.com/sub-processors. We will
              notify partner organizations at least 30 days before adding new
              sub-processors. Partner organizations may object to new
              sub-processors within 14 days of notification.
            </Typography>
          </Stack>

          <Stack sx={{ gap: 2 }}>
            <SubHeading id="7.5" title="Security Measures" />
            <Bullets
              items={[
                'All personal data encrypted in transit using TLS 1.3 or higher',
                'All personal data encrypted at rest using AES-256',
                'Access to personal data restricted to personnel who need it to deliver the service',
                'Regular security assessments and penetration testing',
                'Written Information Security Program maintained in compliance with Massachusetts 201 CMR 17.00'
              ]}
            />
          </Stack>

          {/* Final word */}
          <Divider />
          <Stack sx={{ gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              A Final Word From FraudFront
            </Typography>
            <Typography variant="body1">
              We know this is a lot to read. We tried to make it as clear and
              honest as we could. If something in here {"doesn't"} make sense,
              or feels unfair, or you have a question we {"didn't"} answer —
              please tell us.
            </Typography>
            <Typography variant="body1">
              The people building FraudFront have spent careers at very large
              companies and institutions building systems that protect people.
              We built this because {"we've"} seen what fraud does to families.{' '}
              {"We've"} seen the $38,500 average loss in the FBI data.
            </Typography>
            <Typography variant="body1">
              Iris exists because nobody should face this alone. Your circle of
              trust exists because protection is a family effort. This document
              exists because you deserve to know exactly what {"you're"} signing
              up for.
            </Typography>
          </Stack>

          {/* Contact footer */}
          <Callout title="Contact Us">
            <Stack sx={{ gap: 0.5 }}>
              <Typography variant="body2">
                <strong>Privacy & Data Rights:</strong>{' '}
                <Link href="mailto:privacy@fraud-front.com">
                  privacy@fraud-front.com
                </Link>
              </Typography>
              <Typography variant="body2">
                <strong>Support:</strong>{' '}
                <Link href="mailto:support@fraud-front.com">
                  support@fraud-front.com
                </Link>
              </Typography>
              <Typography variant="body2">
                <strong>Partnerships:</strong>{' '}
                <Link href="mailto:partnerships@fraud-front.com">
                  partnerships@fraud-front.com
                </Link>
              </Typography>
              <Typography variant="body2">
                <strong>General:</strong>{' '}
                <Link href="mailto:hello@fraud-front.com">
                  hello@fraud-front.com
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                FraudFront Inc., Public Benefit Corporation
                <br />
                San Jose, California
              </Typography>
            </Stack>
          </Callout>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            Last updated: June 2026. This document will be updated when our
            practices change. We will notify you by email before any material
            changes take effect.
          </Typography>
        </Stack>
      </Container>
    </PublicLayout>
  );
};

export default Privacy;
